/* DeltaHub Assistant: the chat bubble on deltahub.io (design/support-assistant.md, Phase 3).
 *
 * One file, no dependencies, no build step. Added to the theme as a single deferred tag (snippet.liquid):
 *   <script src="{{ 'dh-assistant.js' | asset_url }}" defer data-api="https://assistant.deltahub.io"></script>
 *
 * Lazy: on load it only reads /v1/config (one GET). The bubble appears if the chat is open for this visitor. Nothing else is fetched, no session is created and
 * no timer-heavy work runs until the visitor opens the panel and sends a first message. It never opens by itself.
 *
 * Backend (plain JSON, no cookies): GET /v1/config, POST /v1/session -> {token}, POST /v1/message {message} with Authorization: Bearer <token>,
 * DELETE /v1/conversation. Anything that goes wrong shows the email-us fallback (support@deltahub.io).
 *
 * Tester mode: opening any page with ?dh_assistant=<token> keeps the token in localStorage and sends it as X-Tester-Token (to /v1/config and /v1/session).
 * ?dh_assistant=off forgets it. The server decides who sees the bubble: hidden when the mode is off, or when it is "testers" and the visitor is not one.
 *
 * Staying clear of the theme: the bubble lifts above a bottom-anchored Shopify cookie banner, and the bubble and panel hide while a cart drawer, a menu
 * drawer, a marketing popup or another dialog is open. Extra selectors can be added with data-avoid="<css selector>" on the script tag.
 *
 * Own styles live in a shadow root, so the theme's CSS cannot reach them and ours cannot leak. Brand: Standard tier tokens, neutral #0A0A0A action, no accent colour. */
(function () {
  "use strict";
  if (window.__dhAssistant) return;
  window.__dhAssistant = true;

  var script = document.currentScript;
  var CFG = {
    api: "https://assistant.deltahub.io",
    email: "support@deltahub.io",
    privacy: "https://deltahub.io/policies/privacy-policy",
    avoid: ""
  };
  var preset = window.DH_ASSISTANT || {};
  Object.keys(preset).forEach(function (k) { CFG[k] = preset[k]; });
  if (script && script.getAttribute("data-api")) CFG.api = script.getAttribute("data-api");
  if (script && script.getAttribute("data-avoid")) CFG.avoid = script.getAttribute("data-avoid");
  CFG.api = String(CFG.api).replace(/\/+$/, "");

  var GREETING = "Hi, I'm DeltaHub's AI assistant. How can I help?";
  var FALLBACK = "I can't help with that in this chat right now. Please email us at " + CFG.email + " and the team will reply, usually within one business day. I'm an AI assistant.";
  var TIMEOUT_MS = 45000;
  var TESTER_KEY = "dh_assistant_tester", CHAT_KEY = "dh_assistant_chat", CFG_KEY = "dh_assistant_cfg";
  var BANNER = '#shopify-pc__banner, .shopify-pc__banner__dialog';       // Shopify's cookie banner: <section id="shopify-pc__banner" class="shopify-pc__banner__dialog"> (seen on deltahub.io, 3 Nov 2026)
  var BLOCKERS = 'cart-drawer[open], cart-notification-drawer[open], dialog[open], [role="dialog"], [aria-modal="true"], [data-dh-avoid], ' +
    '[class*="popup" i], [id*="popup" i], body[class*="klaviyo-prevent-body"], [class*="kl-private-reset-css"], [class*="privy" i], iframe[title*="popup" i], iframe[title*="modal" i]';

  // ---------------------------------------------------------------------------------------------------------------- storage (always guarded)
  var memTester = "";
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* blocked: the flag lives for this page only */ } }
  function lsDel(k) { try { window.localStorage.removeItem(k); } catch (e) { /* ignore */ } }
  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* ignore */ } }
  function ssDel(k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* ignore */ } }

  function testerToken() {
    try {
      var u = new URL(window.location.href), p = u.searchParams.get("dh_assistant");
      if (p !== null) {
        if (/^[A-Za-z0-9._~-]{8,200}$/.test(p)) { lsSet(TESTER_KEY, p); memTester = p; }
        else { lsDel(TESTER_KEY); memTester = ""; }           // "off", empty or malformed: forget it
        u.searchParams.delete("dh_assistant");                // keep the token out of shared links
        window.history.replaceState(window.history.state, "", u.pathname + u.search + u.hash);
      }
    } catch (e) { /* old browser: no URL API */ }
    return lsGet(TESTER_KEY) || memTester || "";
  }
  var tester = testerToken();

  // ---------------------------------------------------------------------------------------------------------------- state
  var S = { show: false, open: false, token: "", exp: 0, msgs: [], dead: false, busy: false, expired: false, turnsLeft: null, maxChars: 1000, apiOpen: true };
  var els = {}, host, root, lastFocus = null, poll = 0, lift = 0, hidden = false;

  function save() {
    ssSet(CHAT_KEY, JSON.stringify({ token: S.token, exp: S.exp, msgs: S.msgs.slice(-60), expired: S.expired }));      // a failure (dead) is not kept: the next page may work
  }
  function restore() {
    try {
      var c = JSON.parse(ssGet(CHAT_KEY) || "null");
      if (c && Array.isArray(c.msgs) && c.msgs.length) {
        S.msgs = c.msgs.filter(function (m) { return m && (m.r === "a" || m.r === "u") && typeof m.t === "string"; });
        S.token = c.exp > Date.now() ? String(c.token || "") : "";
        S.exp = S.token ? c.exp : 0;
        S.expired = !!c.expired || (!!c.token && c.exp <= Date.now());
      }
    } catch (e) { /* ignore a damaged copy */ }
  }

  // ---------------------------------------------------------------------------------------------------------------- network
  function call(method, path, token, body) {
    var h = { Accept: "application/json" };
    if (tester) h["X-Tester-Token"] = tester;
    if (token) h.Authorization = "Bearer " + token;
    if (body !== undefined) h["Content-Type"] = "application/json";
    var ctl = typeof AbortController === "function" ? new AbortController() : null;
    var timer = ctl ? window.setTimeout(function () { ctl.abort(); }, TIMEOUT_MS) : 0;
    return window.fetch(CFG.api + path, {
      method: method, headers: h, mode: "cors", credentials: "omit", cache: "no-store",
      body: body === undefined ? undefined : JSON.stringify(body), signal: ctl ? ctl.signal : undefined
    }).then(function (r) {
      window.clearTimeout(timer);
      return r.json().catch(function () { return {}; }).then(function (j) { return { status: r.status, json: j || {} }; });
    }, function (e) { window.clearTimeout(timer); throw e; });
  }

  // ---------------------------------------------------------------------------------------------------------------- DOM helpers
  function h(tag, attrs, kids) {
    var n = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === "text") n.textContent = attrs[k]; else n.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { n.appendChild(typeof c === "string" ? document.createTextNode(c) : c); });
    return n;
  }
  function icon(path) {
    var s = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    s.setAttribute("viewBox", "0 0 24 24"); s.setAttribute("width", "24"); s.setAttribute("height", "24"); s.setAttribute("aria-hidden", "true"); s.setAttribute("focusable", "false");
    var p = document.createElementNS("http://www.w3.org/2000/svg", "path");
    p.setAttribute("d", path); p.setAttribute("fill", "none"); p.setAttribute("stroke", "currentColor"); p.setAttribute("stroke-width", "1.5");
    p.setAttribute("stroke-linecap", "round"); p.setAttribute("stroke-linejoin", "round");
    s.appendChild(p);
    return s;
  }
  var ICON_CHAT = "M5 4.5h14a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5h-8.2L6.5 20v-3.5H5A1.5 1.5 0 0 1 3.5 15V6A1.5 1.5 0 0 1 5 4.5z";
  var ICON_X = "M6 6l12 12M18 6L6 18";
  var ICON_SEND = "M4 12l16-8-6 16-2.5-6.5L4 12z";

  /** Text with links built as DOM nodes, never as markup: https links and the support address become anchors. */
  function richText(parent, text) {
    var re = /(https?:\/\/[^\s<>"']+)|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g, last = 0, m;
    while ((m = re.exec(text))) {
      var raw = m[0], trail = "";
      while (/[.,;:!?)\]]$/.test(raw)) { trail = raw.slice(-1) + trail; raw = raw.slice(0, -1); }
      if (m.index > last) parent.appendChild(document.createTextNode(text.slice(last, m.index)));
      var a = h("a", { href: m[1] ? raw : "mailto:" + raw, text: raw });
      if (m[1]) { a.setAttribute("target", "_blank"); a.setAttribute("rel", "noopener noreferrer"); }
      parent.appendChild(a);
      if (trail) parent.appendChild(document.createTextNode(trail));
      last = m.index + m[0].length;
    }
    if (last < text.length) parent.appendChild(document.createTextNode(text.slice(last)));
  }

  // ---------------------------------------------------------------------------------------------------------------- styles (Standard tier tokens)
  var CSS = [
    ':host{all:initial;--bg:#F2F2F2;--surface:#FFFFFF;--surface2:#F7F7F7;--text:#0A0A0A;--text2:#5A5A5A;--line:#E5E5E5;--action:#0A0A0A;--action-text:#FFFFFF;',
    '--bottom:16px;--side:16px;font-family:"Neue Haas Grotesk Display Pro","Neue Haas Grotesk Display",Inter,sans-serif;color:var(--text);font-size:16px;line-height:1.4}',
    '*,*::before,*::after{box-sizing:border-box}',
    '.sr{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}',
    'button,textarea{font:inherit;color:inherit}',
    'button{cursor:pointer;-webkit-tap-highlight-color:transparent}',
    ':focus-visible{outline:2px solid var(--action);outline-offset:2px;box-shadow:0 0 0 4px var(--surface)}',
    '.bubble{position:fixed;right:max(var(--side),env(safe-area-inset-right));bottom:calc(var(--bottom) + env(safe-area-inset-bottom));z-index:900;width:56px;height:56px;border-radius:999px;',
    'border:0;background:var(--action);color:var(--action-text);display:flex;align-items:center;justify-content:center;box-shadow:0 8px 32px rgba(10,10,10,.20);transition:transform .15s ease,opacity .15s ease}',
    '.bubble:hover{transform:scale(1.04)}',
    '.bubble:focus-visible{outline-color:var(--action);box-shadow:0 0 0 3px #fff,0 0 0 5px var(--action)}',
    '.bubble svg{width:26px;height:26px}',
    '.panel{position:fixed;right:max(var(--side),env(safe-area-inset-right));bottom:calc(var(--bottom) + env(safe-area-inset-bottom));z-index:2147483000;',
    'width:380px;height:min(600px,calc(var(--vh,100vh) - var(--bottom) - 16px - env(safe-area-inset-bottom)));min-height:320px;display:flex;flex-direction:column;',
    'background:var(--surface);border:1px solid var(--line);border-radius:16px;box-shadow:0 16px 48px rgba(10,10,10,.22);overflow:hidden}',
    '[hidden],:host(.host-off){display:none!important}',
    '.head{display:flex;align-items:center;justify-content:space-between;padding:8px 8px 8px 16px;border-bottom:1px solid var(--line);flex:none}',
    '.head h2{margin:0;font-size:16px;font-weight:700;line-height:1.2}',
    '.x{width:44px;height:44px;border:0;border-radius:999px;background:transparent;display:flex;align-items:center;justify-content:center}',
    '.x:hover{background:var(--surface2)}',
    '.note{margin:0;padding:8px 16px;background:var(--surface2);border-bottom:1px solid var(--line);font-size:14px;color:var(--text2);flex:none}',
    '.log{flex:1;min-height:0;overflow-y:auto;overscroll-behavior:contain;padding:16px;display:flex;flex-direction:column;gap:8px;background:var(--surface)}',
    '.m{max-width:85%;padding:8px 16px;border-radius:16px;white-space:pre-wrap;overflow-wrap:anywhere;font-size:16px}',
    '.m.a{align-self:flex-start;background:var(--surface2);border:1px solid var(--line);border-bottom-left-radius:4px}',
    '.m.u{align-self:flex-end;background:var(--action);color:var(--action-text);border-bottom-right-radius:4px}',
    '.m a{color:inherit;text-decoration:underline;text-underline-offset:2px}',
    '.typing{display:flex;gap:4px;align-items:center;padding:12px 16px}',
    '.typing i{width:6px;height:6px;border-radius:999px;background:var(--text2);animation:dh 1.2s infinite ease-in-out}',
    '.typing i:nth-child(2){animation-delay:.15s}.typing i:nth-child(3){animation-delay:.3s}',
    '@keyframes dh{0%,60%,100%{opacity:.25}30%{opacity:1}}',
    '.left{margin:0;padding:0 16px 4px;font-size:14px;color:var(--text2);flex:none}',
    '.actions{display:flex;flex-wrap:wrap;gap:8px;padding:0 16px 8px;flex:none}',
    '.btn{min-height:44px;padding:0 16px;border-radius:999px;border:1px solid var(--action);background:var(--action);color:var(--action-text);font-weight:700;display:inline-flex;align-items:center;text-decoration:none}',
    '.btn.ghost{background:var(--surface);color:var(--text)}',
    'form{display:flex;gap:8px;align-items:flex-end;padding:8px 16px;border-top:1px solid var(--line);flex:none}',
    'textarea{flex:1;min-width:0;resize:none;min-height:44px;max-height:120px;padding:10px 12px;border:1px solid var(--line);border-radius:12px;background:var(--surface);font-size:16px;line-height:1.3}',
    'textarea::placeholder{color:var(--text2)}',
    'textarea:disabled{background:var(--surface2)}',
    'form button{flex:none;width:44px;height:44px;border:0;border-radius:999px;background:var(--action);color:var(--action-text);display:flex;align-items:center;justify-content:center}',
    'form button:disabled{opacity:.4;cursor:default}',
    '.foot{display:flex;justify-content:space-between;align-items:center;gap:8px;padding:0 8px 4px 16px;font-size:14px;flex:none}',
    '.foot a,.foot button{min-height:44px;display:inline-flex;align-items:center;padding:0 8px;border:0;background:transparent;color:var(--text2);font-size:14px;text-decoration:underline;text-underline-offset:2px}',
    '@media (max-width:600px){.panel{inset:0 0 var(--lift,0px) 0;right:0;bottom:var(--lift,0px);width:auto;height:var(--vh,100%);max-height:calc(100% - var(--lift,0px));border:0;border-radius:0;box-shadow:none}',
    '.panel{padding-bottom:env(safe-area-inset-bottom)}.m{max-width:90%}}',
    '@media (prefers-reduced-motion:reduce){.bubble{transition:none}.typing i{animation:none;opacity:.6}}'
  ].join("");

  // ---------------------------------------------------------------------------------------------------------------- build
  function build() {
    host = h("div", { id: "dh-assistant-root" });
    root = host.attachShadow({ mode: "open" });
    root.appendChild(h("style", { text: CSS }));

    els.bubble = h("button", { type: "button", class: "bubble", "aria-label": "Chat with DeltaHub Assistant", "aria-haspopup": "dialog", "aria-expanded": "false", hidden: "" }, [icon(ICON_CHAT)]);
    els.panel = h("section", { class: "panel", role: "dialog", "aria-modal": "true", "aria-labelledby": "dh-title", hidden: "" });
    els.close = h("button", { type: "button", class: "x", "aria-label": "Close chat" }, [icon(ICON_X)]);
    els.log = h("div", { class: "log", role: "log", "aria-live": "polite", "aria-relevant": "additions", "aria-label": "Conversation", tabindex: "0" });
    els.left = h("p", { class: "left", hidden: "" });
    els.actions = h("div", { class: "actions", hidden: "" });
    els.input = h("textarea", { id: "dh-input", rows: "1", placeholder: "Type your message", maxlength: String(S.maxChars), autocomplete: "off" });
    els.send = h("button", { type: "submit", "aria-label": "Send message" }, [icon(ICON_SEND)]);
    els.form = h("form", {}, [h("label", { for: "dh-input", class: "sr", text: "Your message" }), els.input, els.send]);
    els.del = h("button", { type: "button", text: "Delete this chat" });
    var privacy = h("a", { href: CFG.privacy, target: "_blank", rel: "noopener noreferrer", text: "Privacy policy" });

    els.panel.appendChild(h("div", { class: "head" }, [h("h2", { id: "dh-title", text: "DeltaHub Assistant" }), els.close]));
    els.panel.appendChild(h("p", { class: "note", text: "You are chatting with an AI. Photos cannot be sent here. To share one, email " + CFG.email + "." }));
    els.panel.appendChild(els.log);
    els.panel.appendChild(els.left);
    els.panel.appendChild(els.actions);
    els.panel.appendChild(els.form);
    els.panel.appendChild(h("div", { class: "foot" }, [privacy, els.del]));
    root.appendChild(els.bubble);
    root.appendChild(els.panel);
    document.body.appendChild(host);

    els.bubble.addEventListener("click", openPanel);
    els.close.addEventListener("click", closePanel);
    els.form.addEventListener("submit", function (e) { e.preventDefault(); send(); });
    els.input.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing) { e.preventDefault(); send(); }
    });
    els.input.addEventListener("input", function () { sizeInput(); els.send.disabled = !els.input.value.trim() || S.busy; });
    els.del.addEventListener("click", deleteChat);
    els.panel.addEventListener("keydown", trap);
    document.addEventListener("keydown", function (e) { if (e.key === "Escape" && S.open && !hidden) { e.stopPropagation(); closePanel(); } }, true);
    document.addEventListener("focusin", function (e) {
      if (S.open && !hidden && !host.contains(e.target)) focusFirst();
    });
    window.addEventListener("resize", layout);
    if (window.visualViewport) window.visualViewport.addEventListener("resize", layout);
    document.addEventListener("visibilitychange", function () { if (!document.hidden) layout(); });
  }

  function sizeInput() {
    els.input.style.height = "auto";
    els.input.style.height = Math.min(els.input.scrollHeight + 2, 120) + "px";
  }

  // ---------------------------------------------------------------------------------------------------------------- messages
  function addMsg(role, text, remember) {
    var n = h("div", { class: "m " + (role === "a" ? "a" : "u") }, [h("span", { class: "sr", text: role === "a" ? "DeltaHub Assistant: " : "You: " })]);
    richText(n, text);
    if (els.typing && els.typing.parentNode) els.log.insertBefore(n, els.typing); else els.log.appendChild(n);
    els.log.scrollTop = els.log.scrollHeight;
    if (remember !== false) { S.msgs.push({ r: role, t: text }); save(); }
  }
  function renderAll() {
    els.log.textContent = "";
    els.typing = null;
    if (!S.msgs.length) S.msgs.push({ r: "a", t: GREETING });
    S.msgs.forEach(function (m) { addMsg(m.r, m.t, false); });
    renderState();
  }
  function typing(on) {
    if (on && !els.typing) {
      els.typing = h("div", { class: "m a typing" }, [h("span", { class: "sr", text: "DeltaHub Assistant is typing" }), h("i"), h("i"), h("i")]);
      els.log.appendChild(els.typing);
      els.log.scrollTop = els.log.scrollHeight;
    } else if (!on && els.typing) { els.typing.remove(); els.typing = null; }
  }
  /** Input, buttons and the "messages left" line for the current state. */
  function renderState() {
    var locked = S.dead || S.expired;
    els.input.disabled = locked || S.busy;
    els.send.disabled = locked || S.busy || !els.input.value.trim();
    els.form.hidden = locked;
    els.actions.textContent = "";
    if (S.dead) {
      els.actions.appendChild(h("a", { class: "btn", href: "mailto:" + CFG.email, text: "Email " + CFG.email }));
      if (S.apiOpen) els.actions.appendChild(newChatButton("ghost"));
    }
    if (S.expired) {
      els.actions.appendChild(newChatButton(""));
      els.actions.appendChild(h("a", { class: "btn ghost", href: "mailto:" + CFG.email, text: "Email us" }));
    }
    els.actions.hidden = !els.actions.firstChild;
    els.del.hidden = !S.token;                                         // nothing stored on the server before the first message
    var low = S.turnsLeft !== null && S.turnsLeft <= 3 && !locked;
    els.left.hidden = !low;
    els.left.textContent = low ? (S.turnsLeft === 1 ? "1 message left in this chat." : S.turnsLeft + " messages left in this chat.") : "";
  }
  function newChatButton(extra) {
    var b = h("button", { type: "button", class: "btn " + extra, text: "Start a new chat" });
    b.addEventListener("click", newChat);
    return b;
  }
  function fail(text) {
    typing(false);
    S.dead = true; S.busy = false;
    addMsg("a", text || FALLBACK);
    save(); renderState();
  }
  function newChat() {
    S.token = ""; S.exp = 0; S.msgs = []; S.dead = false; S.expired = false; S.turnsLeft = null; S.busy = false;
    ssDel(CHAT_KEY);
    renderAll();
    els.input.focus();
  }
  function deleteChat() {
    var token = S.token;
    var done = function () { newChat(); addMsg("a", "This chat was deleted.", false); };
    if (!token) { newChat(); return; }
    call("DELETE", "/v1/conversation", token).then(done, done);       // the server also deletes it after 12 months whatever happens here
  }

  // ---------------------------------------------------------------------------------------------------------------- sending
  function ensureSession() {
    if (S.token && S.exp > Date.now()) return Promise.resolve(S.token);
    S.token = "";
    return call("POST", "/v1/session", "", {}).then(function (r) {
      if (r.status !== 200 || r.json.closed || !r.json.token) {
        var e = new Error(r.status === 429 ? "busy" : "closed");
        e.reply = r.json.closed ? r.json.reply : null;
        e.busy = r.status === 429;
        throw e;
      }
      S.token = r.json.token;
      S.exp = Date.now() + Math.max(60, (r.json.expires_in || 1800) - 30) * 1000;
      save();
      return S.token;
    });
  }
  function send() {
    var text = els.input.value.trim();
    if (!text || S.busy || S.dead || S.expired) return;
    if (text.length > S.maxChars) { els.input.value = text.slice(0, S.maxChars); return; }
    if (S.token && S.exp <= Date.now()) { S.expired = true; S.token = ""; addMsg("a", "This chat has timed out. Start a new chat to continue."); renderState(); return; }
    S.busy = true;
    addMsg("u", text);
    els.input.value = ""; sizeInput();
    renderState();
    typing(true);
    ensureSession().then(function (token) {
      return call("POST", "/v1/message", token, { message: text });
    }).then(function (r) {
      typing(false);
      S.busy = false;
      if (r.status === 401) { S.expired = true; S.token = ""; addMsg("a", "This chat has timed out. Start a new chat to continue."); save(); renderState(); return; }
      if (r.status === 413) { addMsg("a", "That message is too long. Please keep it under " + (r.json.max_chars || S.maxChars) + " characters."); renderState(); els.input.value = text.slice(0, S.maxChars); els.input.focus(); return; }
      if (r.status !== 200 || typeof r.json.reply !== "string") { fail(FALLBACK); return; }
      addMsg("a", r.json.reply);
      if (r.json.closed) { if (r.json.reason === "turn limit") S.expired = true; else S.dead = true; }
      S.turnsLeft = typeof r.json.turns_left === "number" ? r.json.turns_left : S.turnsLeft;
      save(); renderState();
      if (!S.dead && S.open) els.input.focus();
    }).catch(function (e) {
      if (e && e.busy) fail("There have been a lot of chats from this connection. Please try again later, or email us at " + CFG.email + ".");
      else fail((e && e.reply) || FALLBACK);
    });
  }

  // ---------------------------------------------------------------------------------------------------------------- open, close, focus
  function focusables() {
    return Array.prototype.slice.call(els.panel.querySelectorAll('a[href],button,textarea,[tabindex="0"]')).filter(function (n) {
      return !n.disabled && !n.closest("[hidden]") && n.getClientRects().length > 0;
    });
  }
  function focusFirst() {
    var list = focusables();
    var pick = (!els.input.disabled && !els.form.hidden && els.input) || list[0];
    if (pick) pick.focus();
  }
  function trap(e) {
    if (e.key !== "Tab") return;
    var list = focusables();
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1], active = root.activeElement;
    if (e.shiftKey && (active === first || !els.panel.contains(active))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }
  function mobile() { return window.matchMedia && window.matchMedia("(max-width:600px)").matches; }
  function openPanel() {
    if (S.open) return;
    lastFocus = document.activeElement;
    S.open = true;
    els.bubble.hidden = true;
    els.bubble.setAttribute("aria-expanded", "true");
    els.panel.hidden = false;
    if (!els.log.firstChild) renderAll();
    if (mobile()) { document.documentElement.style.overflow = "hidden"; }
    layout();
    window.setTimeout(function () { els.log.scrollTop = els.log.scrollHeight; focusFirst(); }, 0);
  }
  function closePanel() {
    if (!S.open) return;
    S.open = false;
    els.panel.hidden = true;
    els.bubble.hidden = false;
    els.bubble.setAttribute("aria-expanded", "false");
    document.documentElement.style.overflow = "";
    layout();
    els.bubble.focus();
  }

  // ---------------------------------------------------------------------------------------------------------------- staying clear of the theme
  function seen(n) {
    if (!n || n === host || host.contains(n)) return null;
    var r = n.getBoundingClientRect();
    if (r.width < 2 || r.height < 2 || r.bottom <= 0 || r.right <= 0 || r.top >= window.innerHeight || r.left >= window.innerWidth) return null;
    var cs = window.getComputedStyle(n);
    if (cs.visibility === "hidden" || cs.display === "none" || parseFloat(cs.opacity) < 0.05) return null;
    return r;
  }
  /** Reads the page and sets --bottom/--lift (above a bottom-anchored cookie banner) and `hidden` (a drawer, popup or dialog is open). */
  function layout() {
    if (!host || !S.show) return;
    var vh = window.innerHeight, vw = window.innerWidth, newLift = 0, block = false, i, n, r, list;
    list = document.querySelectorAll(BANNER);
    for (i = 0; i < list.length; i++) {
      r = seen(list[i]);
      if (!r) continue;
      var zone = S.open && !mobile() ? 396 : 72;                           // width of what we draw at the right edge: panel or bubble
      if (r.top > vh * 0.45 && r.bottom >= vh - 24) { if (r.right > vw - zone) newLift = Math.max(newLift, Math.ceil(vh - r.top)); }
      else block = true;                                                  // a banner in the middle or on top: leave it alone
    }
    if (newLift > vh * 0.5) block = true;
    if (!block) {
      var sel = BLOCKERS + (CFG.avoid ? ", " + CFG.avoid : "");
      try { list = document.querySelectorAll(sel); } catch (e) { list = document.querySelectorAll(BLOCKERS); }
      for (i = 0; i < list.length && !block; i++) {
        n = list[i];
        if (n.closest && n.closest(BANNER)) continue;
        r = seen(n);
        if (!r) continue;
        var covers = (Math.min(r.right, vw) - Math.max(r.left, 0)) * (Math.min(r.bottom, vh) - Math.max(r.top, 0)) >= vw * vh * 0.12;
        if (covers || n.hasAttribute("data-dh-avoid")) block = true;
      }
    }
    lift = newLift;
    host.style.setProperty("--bottom", (lift ? lift + 12 : 16) + "px");
    host.style.setProperty("--lift", lift + "px");
    host.style.setProperty("--vh", ((mobile() && window.visualViewport ? window.visualViewport.height : vh) - (mobile() ? lift : 0)) + "px");
    if (block !== hidden) {
      hidden = block;
      host.classList.toggle("host-off", hidden);
      document.documentElement.style.overflow = !hidden && S.open && mobile() ? "hidden" : "";
      if (!hidden && S.open) window.setTimeout(focusFirst, 0);
    }
  }
  function startWatching() {
    if (poll) return;
    layout();
    poll = window.setInterval(function () { if (!document.hidden) layout(); }, 1000);
  }

  // ---------------------------------------------------------------------------------------------------------------- start: read the config, nothing else
  function show(openForChat) {
    S.apiOpen = openForChat;
    if (S.show) return;
    S.show = true;
    build();
    restore();
    if (!openForChat) { S.dead = true; S.expired = false; S.msgs.push({ r: "a", t: FALLBACK }); }       // spend cap or database down: only the way to the team
    els.bubble.hidden = false;
    startWatching();
  }
  function start() {
    call("GET", "/v1/config", "").then(function (r) {
      var j = r.json || {};
      if (r.status !== 200) throw new Error("config");
      if (typeof j.max_chars === "number") S.maxChars = j.max_chars;
      var visible = j.show !== undefined ? !!j.show : !!j.open;               // older servers send only `open`
      if (visible) ssSet(CFG_KEY, "1"); else ssDel(CFG_KEY);
      if (visible) show(!!j.open);
    }).catch(function () {
      if (ssGet(CFG_KEY) === "1") show(false);                                  // it was there a moment ago: keep the way to the team open
    });
  }
  function ready() { window.setTimeout(start, 0); }
  if (document.readyState === "complete") ready(); else window.addEventListener("load", ready, { once: true });
})();
